import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  MapPin,
  Phone,
  User,
  CreditCard,
  Truck,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";
import { productAPI, orderAPI } from "../services/api";
import {
  sendOrderConfirmation,
  sendOrderNotificationToShop,
} from "../services/wasender";
import { useSettings } from "../hooks/useSettings";
import FreeDeliveryBanner from "../components/FreeDeliveryBanner";
import toast from "react-hot-toast";

const OrderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getDeliveryLimit, getDeliveryCharge, getFreeDeliveryThreshold } =
    useSettings();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    address: "",
    landmark: "",
    paymentMethod: "cod",
    quantity: 1,
  });

  const [isValidAddress, setIsValidAddress] = useState(null);
  const [distance, setDistance] = useState(null);
  const [isCheckingAddress] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [userCoordinates, setUserCoordinates] = useState(null);
  const [addressValidationTimeout, setAddressValidationTimeout] =
    useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productAPI.getById(id);
        setProduct(response.data);
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (addressValidationTimeout) {
        clearTimeout(addressValidationTimeout);
      }
    };
  }, [addressValidationTimeout]);

  // 🏪 Shop coordinates: [lat, lon]
  const shopCoordinates = [26.76238546469451, 88.41876106544638];
  const deliveryLimit = getDeliveryLimit();
  const deliveryCharge = getDeliveryCharge();
  const freeDeliveryThreshold = getFreeDeliveryThreshold();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If user is manually typing address, validate it with debounce
    if (name === "address") {
      // Clear previous timeout
      if (addressValidationTimeout) {
        clearTimeout(addressValidationTimeout);
      }

      // Reset validation state when address changes
      if (value.trim().length < 10) {
        setIsValidAddress(null);
        setDistance(null);
        setUserCoordinates(null);
      } else if (value.trim().length >= 10) {
        // Set new timeout for validation
        const timeout = setTimeout(() => {
          validateManualAddress(value.trim());
        }, 1000); // Wait 1 second after user stops typing
        setAddressValidationTimeout(timeout);
      }
    }
  };

  // Validate manually typed address using Google Geocoding API
  const validateManualAddress = async (address) => {
    if (!import.meta.env.VITE_GOOGLE_MAPS_KEY) {
      // If no Google Maps API key, allow manual addresses (fallback)
      setIsValidAddress(true);
      setDistance(null);
      return;
    }

    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          address
        )}&key=${import.meta.env.VITE_GOOGLE_MAPS_KEY}&language=en`
      );
      const data = await res.json();

      if (data.status === "OK" && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        const { lat, lng } = location;

        setUserCoordinates({ lat, lon: lng });

        // Calculate distance
        const calculatedDistance = calculateDistance(
          shopCoordinates[0],
          shopCoordinates[1],
          lat,
          lng
        );

        setDistance(calculatedDistance);
        const isValid = calculatedDistance <= deliveryLimit;
        setIsValidAddress(isValid);

        if (!isValid) {
          toast.error(
            `Sorry, you're ${calculatedDistance.toFixed(
              1
            )} km away. Delivery limit is ${deliveryLimit} km.`
          );
        }
      } else {
        // If geocoding fails, allow the address (fallback)
        setIsValidAddress(true);
        setDistance(null);
      }
    } catch (error) {
      console.error("Manual address validation error:", error);
      // If validation fails, allow the address (fallback)
      setIsValidAddress(true);
      setDistance(null);
    }
  };

  // 🧭 Auto-detect location using Google Geocoding API
  const autoDetectLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }

    if (!import.meta.env.VITE_GOOGLE_MAPS_KEY) {
      toast.error("Google Maps API key not configured.");
      return;
    }

    setIsDetectingLocation(true);
    toast.loading("Detecting your location...", { id: "location-detect" });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        console.log("Location detected:", { latitude, longitude });

        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${
              import.meta.env.VITE_GOOGLE_MAPS_KEY
            }&language=en`
          );
          const data = await res.json();
          console.log("Geocoding response:", data);

          if (data.status === "OK" && data.results.length > 0) {
            const detectedAddress = data.results[0].formatted_address;

            setFormData((prev) => ({
              ...prev,
              address: detectedAddress,
            }));

            setUserCoordinates({ lat: latitude, lon: longitude });

            // Calculate distance
            const calculatedDistance = calculateDistance(
              shopCoordinates[0],
              shopCoordinates[1],
              latitude,
              longitude
            );

            setDistance(calculatedDistance);
            const isValid = calculatedDistance <= deliveryLimit;
            setIsValidAddress(isValid);

            toast.dismiss("location-detect");

            if (isValid) {
              toast.success(
                `✅ Location detected! Distance: ${calculatedDistance.toFixed(
                  1
                )} km`
              );
            } else {
              toast.error(
                `❌ Sorry, you're ${calculatedDistance.toFixed(
                  1
                )} km away. Delivery limit is ${deliveryLimit} km.`
              );
            }
          } else {
            toast.dismiss("location-detect");
            toast.error(`Unable to fetch address: ${data.status}`);
            console.error("Geocoding failed:", data);
          }
        } catch (error) {
          console.error("Geocoding error:", error);
          toast.dismiss("location-detect");
          toast.error("Error detecting address. Please try again.");
        } finally {
          setIsDetectingLocation(false);
        }
      },
      (error) => {
        setIsDetectingLocation(false);
        toast.dismiss("location-detect");

        let msg = "Unable to detect your location. ";
        if (error.code === error.PERMISSION_DENIED) {
          msg =
            "Location permission denied. Please allow location access in your browser settings.";
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          msg = "Location information unavailable. Please try again.";
        } else if (error.code === error.TIMEOUT) {
          msg = "Location request timed out. Please try again.";
        }

        toast.error(msg);
        console.error("Geolocation error:", error);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // 🧮 Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    const basicFieldsValid =
      formData.name.trim() !== "" &&
      formData.phone.trim() !== "" &&
      formData.address.trim() !== "" &&
      formData.quantity > 0 &&
      formData.paymentMethod !== "";

    // If basic fields are valid and address is long enough, allow submission
    // This handles manual address entry when auto-detect isn't used
    if (basicFieldsValid && formData.address.trim().length >= 10) {
      // If isValidAddress is null (no validation done yet), allow submission
      // If isValidAddress is true (validation passed), allow submission
      // If isValidAddress is false (validation failed), don't allow submission
      return isValidAddress !== false;
    }

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only block submission if address validation explicitly failed (isValidAddress === false)
    // Allow submission if validation is null (manual address) or true (validated address)
    if (isValidAddress === false) {
      alert(
        `Sorry, delivery is not available to your location. We deliver within ${deliveryLimit} km from our shop.`
      );
      return;
    }

    if (!isFormValid()) {
      alert("Please fill in all required fields.");
      return;
    }

    // Check if offer is still valid (till Dec 31, 2025)
    const offerEndDate = new Date("2025-12-31T23:59:59");
    const currentDate = new Date();

    const subtotal = product.price * formData.quantity;
    // Free delivery if order >= threshold
    const calculatedDeliveryCharge =
      subtotal >= freeDeliveryThreshold ? 0 : deliveryCharge;
    const totalAmount = subtotal + calculatedDeliveryCharge;

    const googleMapsLink = userCoordinates
      ? `https://www.google.com/maps?q=${userCoordinates.lat},${userCoordinates.lon}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          formData.address
        )}`;

    const paymentMethodText =
      formData.paymentMethod === "cod" ? "Cash on Delivery" : "Online Payment";

    // Show loading toast
    const loadingToast = toast.loading("Confirming Order..");

    try {
      // Prepare order details for WASender API
      const orderDetails = {
        customerPhone: formData.phone.replace(/\D/g, ""),
        customerName: formData.name,
        productName: product.name,
        bagSize: product.weight,
        quantity: formData.quantity,
        pricePerBag: product.price,
        subtotal: subtotal,
        deliveryCharge: calculatedDeliveryCharge,
        total: totalAmount,
        address: formData.address,
        landmark: formData.landmark,
        paymentMethod: paymentMethodText,
        distance: distance,
        googleMapsLink: googleMapsLink,
        shopPhone: import.meta.env.VITE_SHOP_WHATSAPP || "918250676762",
      };

      // Prepare order data for temporary storage (don't save to database yet)
      // Order will only be saved when user reaches the confirmation page
      const orderData = {
        productId: product._id,
        customerName: formData.name,
        customerPhone: formData.phone.replace(/\D/g, ""),
        productName: product.name,
        bagSize: product.weight,
        quantity: formData.quantity,
        pricePerBag: product.price,
        subtotal: subtotal,
        deliveryCharge: calculatedDeliveryCharge,
        total: totalAmount,
        address: formData.address,
        landmark: formData.landmark,
        paymentMethod: paymentMethodText,
        distance: distance,
        googleMapsLink: googleMapsLink,
        customerMessageSent: false,
        shopMessageSent: false,
      };

      // Store order data temporarily in localStorage for confirmation page
      localStorage.setItem("pendingOrder", JSON.stringify(orderData));

      //console.log("Sending order via WASender API:", orderDetails);

      // Send confirmation to customer
      const customerResponse = await sendOrderConfirmation(orderDetails);
      //console.log("Customer response:", customerResponse);

      // Send notification to shop
      const shopResponse = await sendOrderNotificationToShop(orderDetails);
      //console.log("Shop response:", shopResponse);

      // Dismiss loading toast
      toast.dismiss(loadingToast);

      // Update order with message status

      // Navigate to confirmation if customer message was sent successfully
      if (customerResponse.success) {
        // Show success message
        if (shopResponse.success) {
          toast.success("Order confirmed! Messages sent successfully.");
        } else {
          // Customer got their confirmation, but shop notification failed
          toast.success("Order confirmed! Customer notified.");
          console.warn("Shop notification failed:", shopResponse.error);
        }

        // Navigate to confirmation page
        navigate("/order-confirmation");
      } else {
        // Customer message failed - this is critical
        toast.error(
          `Failed to send order confirmation: ${customerResponse.error}`
        );
        console.error("WASender API Error:", {
          customerResponse,
          shopResponse,
        });

        // Fallback to WhatsApp web only if customer message failed
        toast.loading("Opening WhatsApp as fallback...", { duration: 2000 });

        const message = `🛒 *New Order - Grainly*

🌾 *Product:* ${product.name}
� *Beag Size:* ${product.weight} per bag
� *Numaber of Bags:* ${formData.quantity} ${
          formData.quantity === 1 ? "bag" : "bags"
        }
💰 *Price per bag:* ₹${product.price}
💵 *Subtotal:* ₹${subtotal}
🚚 *Delivery Charge:* ₹${deliveryCharge}
💵 *Total Amount:* ₹${totalAmount}

👤 *Customer:*
Name: ${formData.name}
Phone: ${formData.phone}

🏠 *Delivery Address:*
${formData.address}
${formData.landmark ? `🏛️ *Landmark:* ${formData.landmark}` : ""}

📍 *User Location Map:* ${googleMapsLink}

💳 *Payment:* ${paymentMethodText}
📏 *Distance from shop:* ${
          distance ? distance.toFixed(1) + " km" : "Calculating..."
        }

Please confirm this order.`;

        setTimeout(() => {
          const whatsappUrl = `https://wa.me/${
            orderDetails.shopPhone
          }?text=${encodeURIComponent(message)}`;
          window.open(whatsappUrl, "_blank");
        }, 2000);
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error("Failed to send order. Please try again.");
      console.error("Order submission error:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#305669]"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Product Not Found
          </h2>
          <Link to="/shop" className="text-[#305669] hover:text-[#1785A]">
            ← Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  // Check if offer is still valid (till Dec 31, 2025)
  const offerEndDate = new Date("2025-12-31T23:59:59");
  const currentDate = new Date();
  const isOfferValid = currentDate <= offerEndDate;

  const subtotal = product.price * formData.quantity;
  // Free delivery if order >= threshold
  const displayDeliveryCharge =
    subtotal >= freeDeliveryThreshold ? 0 : deliveryCharge;

  return (
    <div className="min-h-screen py-8 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Place Your Order
          </h1>
          <p className="text-gray-600">
            Fill in your details to complete the purchase
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-white p-6 rounded-lg shadow-md h-fit">
            <h2 className="text-xl font-semibold mb-4">Order Summary</h2>

            <div className="flex items-center space-x-4 mb-4">
              <img
                src={product.image}
                alt={product.name}
                className="w-16 h-16 object-cover rounded-lg"
              />
              <div className="flex-1">
                <h3 className="font-semibold">{product.name}</h3>
                <p className="text-gray-600 text-sm">
                  {product.weight} • {product.origin}
                </p>
                <p className="text-[#305669] font-semibold">
                  ₹{product.price} per bag
                </p>
              </div>
            </div>

            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between">
                <span>Bag Size:</span>
                <span>{product.weight} per bag</span>
              </div>
              <div className="flex justify-between">
                <span>Number of Bags:</span>
                <span>
                  {formData.quantity} {formData.quantity === 1 ? "bag" : "bags"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Price per bag:</span>
                <span>₹{product.price}</span>
              </div>
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>₹{subtotal}</span>
              </div>
              <div className="flex justify-between">
                <span>Delivery Charge:</span>
                <span
                  className={
                    displayDeliveryCharge === 0
                      ? "text-green-600 font-semibold"
                      : ""
                  }
                >
                  {displayDeliveryCharge === 0
                    ? "FREE"
                    : `₹${displayDeliveryCharge}`}
                </span>
              </div>
              <div className="flex justify-between font-semibold text-lg border-t pt-2">
                <span>Total Amount:</span>
                <span className="text-[#305669]">
                  ₹{subtotal + displayDeliveryCharge}
                </span>
              </div>
            </div>

            {/* Offer Banner */}
            <FreeDeliveryBanner
              variant="order-progress"
              currentAmount={subtotal}
              showProgress={true}
            />

            {!isOfferValid && (
              <div className="mt-4 p-3 bg-[#B7E5CD]/20 rounded-lg">
                <div className="flex items-center space-x-2 text-[#305669]">
                  <Truck className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Delivery Charge: ₹{deliveryCharge} (within {deliveryLimit}{" "}
                    km)
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4" />
                  <span>Full Name *</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <Phone className="h-4 w-4" />
                  <span>Phone Number(WhatsApp) *</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="+91 11111 22222"
                />
              </div>

              {/* Quantity (Number of Bags) */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Number of Bags * (Each bag: {product.weight})
                </label>
                <select
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((qty) => (
                    <option key={qty} value={qty}>
                      {qty} {qty === 1 ? "bag" : "bags"}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Maximum 10 bags per order
                </p>
              </div>

              {/* Address */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="h-4 w-4" />
                  <span>Delivery Address *</span>
                </label>

                <div className="flex space-x-2 mb-2">
                  <button
                    type="button"
                    onClick={autoDetectLocation}
                    disabled={isDetectingLocation}
                    className="flex items-center space-x-2 px-3 py-2 bg-[#305669] text-white rounded-lg hover:bg-[#1785A] disabled:bg-[#8ABEB9] text-sm"
                  >
                    <MapPin className="h-4 w-4" />
                    <span>
                      {isDetectingLocation
                        ? "Detecting..."
                        : "Auto Detect Location"}
                    </span>
                  </button>
                </div>

                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="Enter your complete delivery address or use auto-detect"
                />

                {isCheckingAddress && (
                  <p className="text-sm text-[#305669] mt-1">
                    Checking delivery availability...
                  </p>
                )}

                {/* Address validation status */}
                {formData.address.trim().length >= 10 &&
                  isValidAddress === null && (
                    <div className="mt-2 p-2 rounded-lg text-sm bg-blue-50 text-blue-700 border border-blue-200">
                      <span>📍 Validating address...</span>
                    </div>
                  )}

                {distance !== null && (
                  <div
                    className={`mt-2 p-2 rounded-lg text-sm ${
                      isValidAddress
                        ? "bg-[#B7E5CD]/20 text-[#305669] border border-[#8ABEB9]"
                        : "bg-red-50 text-red-700 border border-red-200"
                    }`}
                  >
                    {isValidAddress ? (
                      <span>
                        ✓ Delivery available! Distance: {distance.toFixed(1)} km
                      </span>
                    ) : (
                      <span>
                        <AlertCircle className="h-4 w-4 inline" /> Sorry,
                        delivery not available ({distance.toFixed(1)} km)
                      </span>
                    )}
                  </div>
                )}

                {/* Fallback message for manual addresses */}
                {formData.address.trim().length >= 10 &&
                  isValidAddress === null &&
                  !import.meta.env.VITE_GOOGLE_MAPS_KEY && (
                    <div className="mt-2 p-2 rounded-lg text-sm bg-green-50 text-green-700 border border-green-200">
                      <span>
                        ✓ Address accepted. We'll verify delivery availability
                        when processing your order.
                      </span>
                    </div>
                  )}
              </div>

              {/* Landmark */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="h-4 w-4" />
                  <span>Landmark (Optional)</span>
                </label>
                <input
                  type="text"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#305669] focus:border-transparent"
                  placeholder="e.g., Near City Hospital, Opposite Bus Stand"
                />
              </div>

              {/* Payment */}
              <div>
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-3">
                  <CreditCard className="h-4 w-4" />
                  <span>Payment Method *</span>
                </label>
                <div className="space-y-2">
                  {[
                    { val: "cod", label: "Cash on Delivery (COD)" },
                    { val: "online", label: "Online Payment" },
                  ].map((opt) => (
                    <label
                      key={opt.val}
                      className="flex items-center space-x-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={opt.val}
                        checked={formData.paymentMethod === opt.val}
                        onChange={handleInputChange}
                        className="text-[#305669]"
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Submit */}
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={!isFormValid()}
                  className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-colors duration-200 ${
                    isFormValid()
                      ? "bg-[#305669] text-white hover:bg-[#1785A]"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Confirm Order
                </button>
                <Link
                  to={`/product/${product._id}`}
                  className="flex items-center space-x-2 bg-gray-200 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-300 transition-colors duration-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Back</span>
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderForm;
